<template>
  <el-row :gutter="10">
    <el-col :xs="24" :md="12">
      <span>{{ formTitle }}</span>
    </el-col>
    <el-col :xs="24" :md="12"
      v-if="isEdit"
      class="right"
      >
      <el-tooltip
        :content="$t('api.transBtn')"
        placement="top"
        >
        <el-button
          v-if="hasTrans"
          class="is-circle"
          @click="toggleTrans"
          :type="showTrans ? 'primary' : ''"
          >
          <svg-icon icon-class="language"></svg-icon>
        </el-button>
      </el-tooltip>
      <el-tooltip
        :content="$t('api.editLog.btn')"
        placement="top"
        >
        <el-button
          v-if="hasLog"
          circle
          icon="el-icon-tickets"
          @click="showLogList"
          >
        </el-button>
      </el-tooltip>
    </el-col>
    <el-dialog
      :title="$t('api.editLog.title')"
      :visible.sync="logListVis"
      :append-to-body="true"
      :width="isDetailLog ? '80%' : ''"
    >
      <el-table stripe border fit
        :data="logList"
        v-loading.body="logListLoading"
        style="width: 100%"
      >
        <list-index
          :listQuery="logListQuery">
        </list-index>
        <el-table-column
          align="center"
          :label="$t('api.editLog.platform.label')"
          :width="90"
        >
          <template slot-scope="scope">
            <span
              v-html="getPlatformLabel(scope.row)"
            >
            </span>
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          :label="$t('api.user.id')"
          :width="140"
        >
          <template slot-scope="scope">
            <span
              v-if="scope.row.uid"
              @click="doCopy(scope.row.uid)"
              class="allow-click-copy"
            >
              {{ scope.row.uid }}
            </span>
            <span v-else>
              -
            </span>
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          :label="$t('api.editLog.fields.operator.label')"
          :width="200"
        >
          <template slot-scope="scope">
            <span class="user-info">
              <span v-if="scope.row.display_name">
                <el-tooltip
                  class="item"
                  :content="scope.row.display_name"
                  :open-delay="500"
                >
                  <el-tag
                    class="allow-click-copy"
                    @click="doCopy(scope.row.display_name)"
                    effect="plain"
                    type="info"
                  >
                    {{ scope.row.display_name }}
                  </el-tag>
                </el-tooltip>
                <br />
              </span>
              <span v-if="scope.row.email">
                <el-tooltip
                  class="item"
                  :content="scope.row.email"
                  :open-delay="500"
                >
                  <el-tag
                    class="allow-click-copy"
                    @click="doCopy(scope.row.email)"
                    effect="plain"
                    type="info"
                  >
                    <i class="el-icon-message"></i>
                    {{ scope.row.email }}
                  </el-tag>
                </el-tooltip>
              </span>
              <span v-else>
                -
              </span>
            </span>
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          :label="$t('api.editLog.action.label')"
          :width="80"
        >
          <template slot-scope="scope">
            <span
              v-text="getLabel('action', scope.row.action)">
            </span>
          </template>
        </el-table-column>
        <el-table-column
          v-if="isDetailLog"
          :label="$t('api.editLog.fields.orderInfo.label')"
        >
          <template slot-scope="scope">
            <orders-log
              :inputData="scope.row.content"
              :tzLocation="tzLocation"
            >
            </orders-log>
          </template>
        </el-table-column>
        <time-field
          :isForm="false"
          base="editLog"
          name="created_at"
          :width="160"
          >
        </time-field>
        <el-table-column
          align="center"
          :label="$t('api.editLog.detail.label')"
          :width="60"
          >
          <template slot-scope="scope">
            <el-tooltip
              :content="$t('api.editLog.detail.btn')"
              placement="top"
              >
              <el-button
                type="primary"
                size="mini"
                icon="el-icon-tickets"
                @click="showLogDetail(scope.row.id)"
                circle
                >
              </el-button>
            </el-tooltip>
            <el-dialog
              :title="$t('api.editLog.detail.title')"
              :visible.sync="logDetailVis"
              :append-to-body="true"
            >
              <el-form
                label-position="top"
                v-loading="logDetailLoading"
                >
                <el-form-item
                  :label="$t('api.editLog.tableName.label')"
                  >
                  <el-input
                    v-model="logDetail.data_name"
                    readonly
                    >
                  </el-input>
                </el-form-item>
                <el-form-item
                  :label="$t('api.editLog.content.label')"
                  >
                  <el-input
                    type="textarea"
                    :autosize="{ minRows: 8, maxRows: 50}"
                    placeholder=""
                    v-model="logDetail.content"
                    readonly
                    >
                  </el-input>
                </el-form-item>
              </el-form>
            </el-dialog>
          </template>
        </el-table-column>
      </el-table>
      <pager
        updateType="query"
        :listQuery="logListQuery"
        :total="logListTotal"
        :getList="getLogList"
        :pageSizes="[5,10,20]"
        layout="total, sizes, prev, next, jumper"
        ref="pager"
      >
      </pager>
    </el-dialog>
  </el-row>
</template>

<script>

import OrdersLog from '@/components/OrdersLog'
import { doCopy } from '@/utils/copy'
import { getMap, getMapLabel, transText, getEditLogMap } from '@/utils/i18n'
import {
  fetchList as fetchLogList,
  fetchOne as fetchLogDetail,
  fetchBaseOne
} from '@/api/editlog/get'
import { OrdersSource, PlatformType } from '@/enums'

export default {
  name: 'form-title',
  components: {
    OrdersLog
  },
  props: {
    base: {
      type: String,
      required: true
    },
    tzLocation: {
      type: String,
      default: ''
    },
    isEdit: {
      type: Boolean,
      default: true
    },
    hasTrans: {
      type: Boolean,
      default: false
    },
    hasLog: {
      type: Boolean,
      default: true
    },
    showTrans: {
      type: Boolean,
      default: false
    },
    setTrans: {
      type: Function,
      required: false
    },
    path: {
      type: String
    },
    title: {
      type: String,
      default: ''
    },
    logDataID: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      apiPath: this.path || this.base,
      dataId: 0,
      logList: [],
      logDetail: '',
      logListQuery: {
        page: 1,
        limit: 5
      },
      logListTotal: 0,
      logListLoading: false,
      logListVis: false,
      logDetailVis: false,
      logDetailLoading: false
    }
  },
  created () {
    this.map = this.getEditLogMap()
  },
  mounted () {
  },
  computed: {
    isDetailLog () {
      return this.apiPath === 'orders'
    },
    formTitle () {
      if (this.title) {
        return this.title
      }

      return this.transText(this.base + '.title')
    }
  },
  methods: {
    doCopy,
    getMap,
    getMapLabel,
    transText,
    getEditLogMap,
    showLogDetail (id) {
      this.logDetail = ''
      this.logDetailLoading = true
      this.logDetailVis = true
      if (this.isDetailLog) {
        fetchBaseOne(this.apiPath, id).then(res => {
          if (typeof res.data !== 'undefined') {
            this.logDetail = res.data
            this.logDetail.table_name = this.getTypeText(this.logDetail.table_name)
          }
        }).catch(err => {
          console.log(err)
        }).then(() => {
          this.logDetailLoading = false
        })
      } else {
        fetchLogDetail(id).then(res => {
          if (typeof res.data !== 'undefined') {
            this.logDetail = res.data
            this.logDetail.table_name = this.getTypeText(this.logDetail.table_name)
          }
        }).catch(err => {
          console.log(err)
        }).then(() => {
          this.logDetailLoading = false
        })
      }
    },
    toggleTrans () {
      this.setTrans(!this.showTrans)
    },
    showLogList () {
      this.logListVis = true
      this.dataId = (this.logDataID) ? this.logDataID : this.$route.params && this.$route.params.id
      this.getLogList()
    },
    getTypeText (tableName) {
      switch (tableName) {
        case 'AdminUser':
        case 'BossNowUser':
        case 'Member':
          return this.$t('api.editLog.tableName.map.user')
        case 'AdminUserAuth':
        case 'BossNowUserAuth':
        case 'MemberAuth':
          return this.$t('api.editLog.tableName.map.auth')
      }
      return tableName
    },
    isAsFunNowPlatform (p) {
      switch (p) {
        case PlatformType.FunNow:
        case PlatformType.Partner:
        case PlatformType.RwG:
          return true
      }
      return false
    },
    isDisplaySource (s, p) {
      switch (p) {
        case PlatformType.Partner:
        case PlatformType.RwG:
          return true
      }
      switch (s) {
        case OrdersSource.FunBookV2:
        case OrdersSource.Niceday:
        case OrdersSource.Eatigo:
          return true
      }
      return false
    },
    getPlatformLabel (row) {
      let p = this.getLabel('platform', row.platform)
      if (this.isAsFunNowPlatform(row.platform) && this.isDetailLog && 'content' in row) {
        let d = JSON.parse(row.content)
        if (d !== null &&
            typeof d === 'object' &&
            'source' in d &&
            this.isDisplaySource(d.source, row.platform)) {
          let oriPlatform = p
          p = this.getMapLabel(this.getMap('ordersSource'), d.source)
          if (row.platform !== PlatformType.FunNow) {
            p += `<br>(${oriPlatform})`
          }
        }
      }
      return p
    },
    getLabel (type, val) {
      if (typeof this.map[type].find(x => x.value === val) !== 'undefined') {
        return this.map[type].find(x => x.value === val).label
      }
      return val
    },
    getLogList () {
      this.logListLoading = true
      fetchLogList(this.apiPath, this.dataId, this.logListQuery).then(res => {
        this.logList = res.data
        this.logListTotal = res.total_count
        this.$nextTick(() => { // prevent refs undefined error
          // update internal page to sync with current page
          if (this.$refs.pager) {
            this.$refs.pager.update(this.logListQuery.page)
          }
        })
      }).catch(err => {
        console.log(err)
      }).then(() => {
        this.logListLoading = false
      })
    }
  }
}
</script>

<style rel="stylesheet/scss" lang="scss">
.user-info {
  span.el-tag {
    margin: 2px 0;
  }
}
</style>
